import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SubmissionState, WorkflowSubmissionReview, WorkflowV1Read } from '@ca/core/models';
import { DialogService, UserNotificationService, UserWorkspaceService, WorkflowSubmissionService, WorkflowV1Service } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'ca-workflow-unpublish',
    templateUrl: './workflow-unpublish.component.html',
    styleUrls: ['./workflow-unpublish.component.scss'],
})
export class WorkflowUnpublishComponent implements OnInit {
    @Input() workflow: WorkflowV1Read;
    unpublishFormGroup: UntypedFormGroup;

    constructor(
        private activeModal: NgbActiveModal,
        private dialogService: DialogService,
        private formBuilder: UntypedFormBuilder,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private userWorkspaceService: UserWorkspaceService,
        private workflowSubmissionService: WorkflowSubmissionService,
        private workflowV1Service: WorkflowV1Service,
    ) {}

    ngOnInit(): void {
        this.unpublishFormGroup = this.formBuilder.group({
            comments: ['', Validators.required],
        });
    }

    submit(): void {
        if (this.unpublishFormGroup.invalid) {
            return;
        }

        const deleteModal = this.dialogService.openConfirm({
            message: this.translateService.instant('WORKFLOWS.DETAIL_PAGE.UNPUBLISH_WORKFLOW_CONFIRMATION_QUESTION'),
            confirmText: this.translateService.instant('WORKFLOWS.UNPUBLISH'),
            cancelText: this.translateService.instant('GENERAL.CANCEL'),
            showCancel: true,
            showClose: true,
            options: { size: 'md' },
        });

        deleteModal.result
            .then(() => {
                this.userNotificationService.showSpinner();

                const workflowSubmissionModel = new WorkflowSubmissionReview();
                workflowSubmissionModel.commentToProducer = this.unpublishFormGroup.controls.comments.value;
                workflowSubmissionModel.state = SubmissionState.unpublished;

                this.workflowV1Service
                    .getSubmissions(this.workflow.id)
                    .pipe(
                        switchMap(submissions => this.workflowSubmissionService.review$(submissions[0].id, workflowSubmissionModel)),
                        tap(() => this.userNotificationService.showSuccess('WORKFLOWS.DETAIL_PAGE.UNPUBLISH_SUCCESS')),
                        switchMap(() => this.workflowV1Service.getById(this.workflow.id)),
                        tap((workflow: WorkflowV1Read) => this.activeModal.close(workflow)),
                        switchMap(() => this.userWorkspaceService.updateMyWorkflows$()),
                        tap(() => this.userNotificationService.hideSpinner()),
                        catchError(() => {
                            this.userNotificationService.hideSpinner();
                            this.userNotificationService.showError('WORKFLOWS.DETAIL_PAGE.UNPUBLISH_FAIL');
                            return EMPTY;
                        }),
                    )
                    .subscribe();
            })
            .catch(() => {
                // do nothing
            });
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }
}
